import React from "react";
import { isEmpty, isNil, partial } from "ramda";
import { connect } from "react-redux";
import { Button } from "react-toolbox/lib/button";
import { FontIcon } from "react-toolbox/lib/font_icon";
import { firstError } from "util/validation";
import {
  changeModuleCardField,
  changeNewModuleCardThumbnail,
  publishNewModule,
  saveNewModuleCardField,
  saveNewModuleCardFieldSuggestAsInternalCode,
  showEditModuleCardThumbnail,
  syncModuleCardField,
  syncModuleCardThumbnail,
} from "manage-modules/redux/manageModulesActions";
import DiscardNewModuleConfirmationDialog from "manage-modules/components/dialog/DiscardNewModuleConfirmationDialog";
import ModuleItemsRemoveConfirmationDialog from "manage-modules/components/dialog/ModuleItemsRemoveConfirmationDialog";
import InputDarkBackground from "components/InputDarkBackground";
import PanelLoadingIndicator from "components/PanelLoadingIndicator";
import ModuleTabs from "manage-modules/components/card/ModuleTabs";
import styles from "manage-modules/components/card/ModuleCard.css";

const ModuleCard = ({
  errors,
  fetchingBase64EncodedThumbnailUrl,
  fields,
  isNew,
  isThumbnailSyncing,
  moduleId,
  onChangeField,
  onChangeNewThumbnail,
  onMouseEnterThumbnail,
  onMouseLeaveThumbnail,
  onPublishModule,
  onSaveField,
  onSaveSuggestInternalCode,
  onSetNewModuleCardName,
  onSyncField,
  onSyncThumbnail,
  showEditThumbnail,
  synced,
  syncingFields,
}) => (
  <div className={styles.container}>
    <div className={styles.header}>
      <div className={styles.thumbnailContainer}>
        <label
          className={styles.thumbnailLabel}
          htmlFor="thumbnail-file-input"
          onMouseEnter={onMouseEnterThumbnail}
          onMouseLeave={onMouseLeaveThumbnail}
          onClick={e => {
            if (isThumbnailSyncing) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          {fields.iconUrl ? (
            <img
              alt="Module thumbnail"
              className={styles.thumbnailImage}
              {...fields.iconUrl && { src: fields.iconUrl }}
            />
          ) : (
            !showEditThumbnail && (
              <FontIcon
                className={
                  isNil(errors["iconUrl"])
                    ? styles.thumbnailNewIcon
                    : styles.thumbnailNewIconError
                }
                value="landscape"
              />
            )
          )}

          {showEditThumbnail &&
            fields.iconUrl && (
              <FontIcon className={styles.thumbnailEditIcon} value="edit" />
            )}

          {showEditThumbnail &&
            !fields.iconUrl && (
              <FontIcon className={styles.thumbnailNewIcon} value="edit" />
            )}

          {isThumbnailSyncing && <PanelLoadingIndicator />}
        </label>

        <input
          key={!isNew && "thumbnailFileInput-" + moduleId}
          className={styles.thumbnailFileInput}
          id="thumbnail-file-input"
          type="file"
          accept=".png"
          onChange={isNew ? onChangeNewThumbnail : onSyncThumbnail}
        />
      </div>
      <div className={styles.namesContainer}>
        <h2>{moduleId}</h2>
        <InputDarkBackground
          className={styles.moduleName}
          label="Name"
          hint="Enter a new module name..."
          value={fields.name}
          onBlur={
            isNew
              ? partial(onSaveSuggestInternalCode, ["name", fields["name"]])
              : partial(onSyncField, ["name", fields["name"]])
          }
          onChange={partial(onChangeField, ["name"])}
          error={firstError(errors, "name")}
          syncing={!isNil(syncingFields["name"])}
        />
        <InputDarkBackground
          className={styles.displayName}
          label="Display Name"
          hint="Enter a new module display name..."
          value={fields.displayName}
          onBlur={
            isNew
              ? partial(onSaveField, ["displayName", fields["displayName"]])
              : partial(onSyncField, ["displayName", fields["displayName"]])
          }
          onChange={partial(onChangeField, ["displayName"])}
          error={firstError(errors, "displayName")}
          syncing={!isNil(syncingFields["displayName"])}
        />
        {isNew && (
          <div className={styles.newModuleActionsContainer}>
            <Button label="Publish" raised onClick={onPublishModule} />
          </div>
        )}
      </div>
    </div>
    <div className={styles.tabsContainer}>
      <ModuleTabs />
    </div>
    <DiscardNewModuleConfirmationDialog />
    <ModuleItemsRemoveConfirmationDialog />
  </div>
);

let newModuleThumbnailFileRef = null; // ahhhhh!

const getFileFromFileInputEvent = event => {
  const fileInput = event.target;
  if (fileInput.files && fileInput.files[0]) {
    return fileInput.files[0];
  }
};

const mapStateToProps = state => {
  const {
    errors,
    fetchingBase64EncodedThumbnailUrl,
    fields,
    itemsSynced,
    module,
    showEditThumbnail,
  } = state.manageModules.moduleCard;

  if (!isNil(module.id)) {
    newModuleThumbnailFileRef = null;
  }

  const syncingFields = state.manageModules.syncingFields[module.id] || {};
  const isThumbnailSyncing =
    fetchingBase64EncodedThumbnailUrl || !isNil(syncingFields["iconUrl"]);

  return {
    errors,
    isNew: !module.id,
    isThumbnailSyncing,
    fetchingBase64EncodedThumbnailUrl,
    fields,
    moduleId: module.id,
    showEditThumbnail: showEditThumbnail && !isThumbnailSyncing,
    synced: itemsSynced && isEmpty(syncingFields),
    syncingFields,
  };
};

const mapDispatchToProps = dispatch => ({
  onChangeField: (field, value) =>
    dispatch(changeModuleCardField(field, value)),
  onChangeNewThumbnail: event => {
    const file = getFileFromFileInputEvent(event);
    if (file) {
      newModuleThumbnailFileRef = file;
      dispatch(changeNewModuleCardThumbnail(file));
    }
  },
  onPublishModule: () => dispatch(publishNewModule(newModuleThumbnailFileRef)),
  onMouseEnterThumbnail: () => dispatch(showEditModuleCardThumbnail(true)),
  onMouseLeaveThumbnail: () => dispatch(showEditModuleCardThumbnail(false)),
  onSaveField: (field, value) => dispatch(saveNewModuleCardField(field, value)),
  onSaveSuggestInternalCode: (field, value) =>
    dispatch(saveNewModuleCardFieldSuggestAsInternalCode(field, value)),
  onSyncField: (field, value) => dispatch(syncModuleCardField(field, value)),
  onSyncThumbnail: event => {
    const file = getFileFromFileInputEvent(event);
    if (file) {
      dispatch(syncModuleCardThumbnail(file));
    }
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ModuleCard);
